import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  ArrowLeft, Loader2, Sparkles, Users, Calendar, Brain, 
  Target, Lightbulb, Wrench, TestTube, Share2, CheckCircle,
  ExternalLink, BookOpen, Link as LinkIcon, Download, FileText
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ImpactLabAnalysis = () => {
  const { challengeId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [loadingStage, setLoadingStage] = useState('');
  const [creating, setCreating] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [challenge, setChallenge] = useState(null);

  useEffect(() => {
    fetchAnalysis();
  }, [challengeId]);

  const fetchAnalysis = async () => {
    try {
      setLoading(true);
      
      // Stage 1: Fetch challenge details
      setLoadingStage('Loading challenge details...');
      const challengeRes = await axios.get(`${BACKEND_URL}/api/challenges/${challengeId}`);
      setChallenge(challengeRes.data);
      
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Stage 2: Researching landscape
      setLoadingStage('Researching current landscape and existing solutions...');
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Stage 3: Finding precedents
      setLoadingStage('Analyzing successful precedents and case studies...');
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Stage 4: Generating frameworks
      setLoadingStage('Generating solution frameworks and approaches...');
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Stage 5: Creating roadmap
      setLoadingStage('Creating 7-day IDEO design thinking roadmap...');
      
      // Generate Impact Lab analysis
      const analysisRes = await axios.post(
        `${BACKEND_URL}/api/impact-lab/analyze?challenge_id=${challengeId}`,
        {},
        { withCredentials: true }
      );
      
      await new Promise(resolve => setTimeout(resolve, 500));
      setLoadingStage('Finalizing analysis...');
      
      setAnalysis(analysisRes.data.analysis);
    } catch (error) {
      console.error('Error fetching analysis:', error);
      alert('Failed to generate Impact Lab analysis');
    } finally {
      setLoading(false);
      setLoadingStage('');
    }
  };

  const handleCreateImpactLab = async () => {
    try {
      setCreating(true);
      
      const response = await axios.post(
        `${BACKEND_URL}/api/impact-lab/create?challenge_id=${challengeId}`,
        {},
        { withCredentials: true }
      );
      
      if (response.data.success) {
        const labId = response.data.impact_lab.id;
        navigate(`/impact-lab/${labId}`);
      }
    } catch (error) {
      console.error('Error creating Impact Lab:', error);
      alert(error.response?.data?.detail || 'Failed to create Impact Lab');
    } finally {
      setCreating(false);
    }
  };

  const handleExportMarkdown = () => {
    if (!analysis || !challenge) return;
    
    let markdown = `# Impact Lab Analysis: ${challenge.title}\n\n`;
    markdown += `**Challenge Type:** ${challenge.challenge_type || 'SDG'}\n`;
    markdown += `**Geographic Scope:** ${challenge.geographic_scope || 'Global'}\n\n`;
    markdown += `---\n\n`;
    
    // Secondary Research
    markdown += `## Secondary Research & Insights\n\n`;
    markdown += `${analysis.secondary_research}\n\n`;
    markdown += `---\n\n`;
    
    // Suggested Personas
    markdown += `## Suggested Team Personas\n\n`;
    markdown += `Recommended backgrounds and expertise for your Impact Lab team (6-12 members):\n\n`;
    analysis.suggested_personas.forEach((persona, i) => {
      markdown += `### ${i + 1}. ${persona.role}\n`;
      markdown += `**Why:** ${persona.why}\n\n`;
    });
    markdown += `---\n\n`;
    
    // 7-Day Roadmap
    markdown += `## 7-Day IDEO Design Thinking Roadmap\n\n`;
    analysis.seven_day_roadmap.forEach((day) => {
      markdown += `### Day ${day.day}: ${day.title}\n`;
      markdown += `**IDEO Phase:** ${day.ideo_phase}\n\n`;
      markdown += `**Objectives:**\n`;
      day.objectives.forEach(obj => markdown += `- ${obj}\n`);
      markdown += `\n**Activities:**\n`;
      day.activities.forEach(act => markdown += `- ${act}\n`);
      markdown += `\n**Deliverables:**\n`;
      day.deliverables.forEach(del => markdown += `- ${del}\n`);
      markdown += `\n`;
    });
    markdown += `---\n\n`;
    
    // Resources
    if (analysis.resources && analysis.resources.length > 0) {
      markdown += `## Resources\n\n`;
      analysis.resources.forEach((resource, i) => {
        markdown += `${i + 1}. **${resource.title}** (${resource.type})\n`;
        markdown += `   ${resource.url}\n\n`;
      });
      markdown += `---\n\n`;
    }
    
    // Think Lab Exercises
    if (analysis.think_lab_exercises && analysis.think_lab_exercises.length > 0) {
      markdown += `## Think Lab Exercises\n\n`;
      analysis.think_lab_exercises.forEach((exercise, i) => {
        markdown += `${i + 1}. ${exercise}\n`;
      });
      markdown += `\n`;
    }
    
    markdown += `---\n\n`;
    markdown += `*Powered by IDEO's design thinking methodology*\n`;
    markdown += `*Generated by Idea Fountain Impact Lab*\n`;
    
    // Download
    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `impact-lab-analysis-${challenge.title.replace(/\s+/g, '-').toLowerCase()}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const dayIcons = [Target, Brain, Lightbulb, Sparkles, Wrench, TestTube, Share2];

  const getResourceIcon = (type) => {
    switch(type) {
      case 'paper': return FileText;
      case 'report': return FileText;
      case 'organization': return Users;
      case 'toolkit': return Wrench;
      case 'data': return Target;
      default: return LinkIcon;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <Loader2 className="w-16 h-16 animate-spin text-purple-600 mx-auto mb-6" />
          <h3 className="text-xl font-bold text-gray-800 mb-2">Analyzing Challenge with GPT-5</h3>
          <p className="text-gray-600 mb-6">{loadingStage}</p>
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-purple-600 to-pink-600 animate-pulse" style={{ width: '70%' }}></div>
          </div>
          <p className="text-xs text-gray-500 mt-4">
            Generating IDEO-inspired roadmap with research, precedents, and solutions
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      {/* Header */}
      <header className="border-b bg-white/90 backdrop-blur-md sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate(`/challenges/${challengeId}`)}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <h1 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              Impact Lab Analysis
            </h1>
            <div className="w-24"></div>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto px-6 py-12 max-w-6xl">
        {/* Challenge Info */}
        {challenge && (
          <Card className="mb-8 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-bold mb-2">{challenge.title}</h2>
              <p className="opacity-90">{challenge.description}</p>
            </CardContent>
          </Card>
        )}

        {/* IDEO Attribution */}
        <div className="mb-8 p-4 bg-white rounded-lg border-l-4 border-purple-500">
          <p className="text-sm text-gray-700">
            <strong>Powered by IDEO's design thinking methodology</strong> — This analysis follows the proven 7-step 
            design thinking process developed by IDEO, a global leader in human-centered innovation.
          </p>
        </div>

        {analysis && (
          <>
            {/* Secondary Research */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Brain className="w-5 h-5 text-purple-600" />
                  Secondary Research & Insights
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 whitespace-pre-line leading-relaxed">
                  {analysis.secondary_research}
                </p>
              </CardContent>
            </Card>

            {/* Suggested Personas */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-pink-600" />
                  Suggested Team Personas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4">
                  Recommended backgrounds and expertise for your Impact Lab team (6-12 members):
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  {analysis.suggested_personas.map((persona, index) => (
                    <div key={index} className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200">
                      <div className="font-semibold text-purple-900 mb-2">{persona.role}</div>
                      <p className="text-sm text-gray-700">{persona.why}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* 7-Day IDEO Roadmap */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-orange-600" />
                  7-Day IDEO Design Thinking Roadmap
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-6">
                  Structured sprint following IDEO's proven design thinking process
                </p>
                <div className="space-y-6">
                  {analysis.seven_day_roadmap.map((day, index) => {
                    const Icon = dayIcons[index] || CheckCircle;
                    return (
                      <div key={index} className="relative pl-12 pb-6 border-l-2 border-purple-200 last:border-0">
                        {/* Day Badge */}
                        <div className="absolute left-0 top-0 -ml-4 w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                          {day.day}
                        </div>
                        
                        <div className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-md transition-shadow">
                          <div className="flex items-start gap-3 mb-3">
                            <Icon className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
                            <div>
                              <div className="text-xs font-semibold text-purple-600 uppercase tracking-wide mb-1">
                                {day.ideo_phase}
                              </div>
                              <h4 className="font-bold text-lg text-gray-900">{day.title}</h4>
                            </div>
                          </div>
                          
                          <div className="space-y-3">
                            <div>
                              <div className="text-sm font-semibold text-gray-700 mb-1">Objectives:</div>
                              <ul className="text-sm text-gray-600 space-y-1">
                                {day.objectives.map((obj, i) => (
                                  <li key={i} className="flex items-start gap-2">
                                    <span className="text-purple-600 mt-1">•</span>
                                    <span>{obj}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                            
                            <div>
                              <div className="text-sm font-semibold text-gray-700 mb-1">Activities:</div>
                              <ul className="text-sm text-gray-600 space-y-1">
                                {day.activities.map((activity, i) => (
                                  <li key={i} className="flex items-start gap-2">
                                    <span className="text-pink-600 mt-1">→</span>
                                    <span>{activity}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                            
                            <div>
                              <div className="text-sm font-semibold text-gray-700 mb-1">Deliverables:</div>
                              <div className="flex flex-wrap gap-2">
                                {day.deliverables.map((deliverable, i) => (
                                  <span key={i} className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                                    {deliverable}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Resources Section */}
            {analysis.resources && analysis.resources.length > 0 && (
              <Card className="mb-8">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-emerald-600" />
                    Resources & References
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-4">
                    Curated resources to inform your Impact Lab journey:
                  </p>
                  <div className="grid md:grid-cols-2 gap-3">
                    {analysis.resources.map((resource, index) => {
                      const Icon = getResourceIcon(resource.type);
                      return (
                        <a
                          key={index}
                          href={resource.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-start gap-3 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg border border-emerald-200 hover:shadow-md transition-shadow group"
                        >
                          <Icon className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-gray-900 group-hover:text-emerald-700 transition-colors line-clamp-2">
                              {resource.title}
                            </div>
                            <div className="text-xs text-gray-500 uppercase tracking-wide mt-1">
                              {resource.type}
                            </div>
                          </div>
                          <ExternalLink className="w-4 h-4 text-emerald-600 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </a>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Think Lab Exercises */}
            {analysis.think_lab_exercises && analysis.think_lab_exercises.length > 0 && (
              <Card className="mb-8">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-amber-600" />
                    Think Lab Exercises
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-4">
                    Suggested exercises to deepen your team's understanding and generate better solutions:
                  </p>
                  <div className="space-y-3">
                    {analysis.think_lab_exercises.map((exercise, index) => (
                      <div
                        key={index}
                        className="flex items-start gap-3 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200"
                      >
                        <div className="flex-shrink-0 w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                          {index + 1}
                        </div>
                        <p className="text-gray-700 flex-1">{exercise}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Export Button */}
            <div className="mb-8 text-center">
              <Button
                onClick={handleExportMarkdown}
                variant="outline"
                className="border-purple-600 text-purple-600 hover:bg-purple-50"
              >
                <Download className="w-4 h-4 mr-2" />
                Export Analysis (Markdown)
              </Button>
            </div>

            {/* CTA */}
            <div className="text-center">
              <Button
                onClick={handleCreateImpactLab}
                disabled={creating}
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-lg px-8 py-6"
              >
                {creating ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Creating Impact Lab...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Launch Impact Lab & Build Team
                  </>
                )}
              </Button>
              <p className="text-sm text-gray-600 mt-4">
                Start your 7-day design thinking sprint and invite 6-12 team members
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ImpactLabAnalysis;
